import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardBody,
  CardTitle,
  Form,
  Button,
  Alert,
  Spinner,
  Row,
  Col
} from 'reactstrap';
import instance from '../api/Axios';

const CreateBursary = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        name: '',
        funder: '',
        bmo_bursary_code: '',
        start_date: '',
        end_date: '',
        bursary_currency: '',
        funding_amount: '',
        number_of_beneficiaries: '',
        bursary_manager: ''
    });

    const [funders, setFunders] = useState([]);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [currentUserId, setCurrentUserId] = useState(null);

    // Currency options
    const currencyOptions = [
        { value: 'USD', label: 'USD - US Dollar' },
        { value: 'EUR', label: 'EUR - Euro' },
        { value: 'ZAR', label: 'ZAR - South African Rand' },
        { value: 'GBP', label: 'GBP - British Pound' },
        { value: 'CAD', label: 'CAD - Canadian Dollar' },
        { value: 'AUD', label: 'AUD - Australian Dollar' },
        { value: 'JPY', label: 'JPY - Japanese Yen' },
        { value: 'CHF', label: 'CHF - Swiss Franc' }
    ];

    const fetchAllFunders = useCallback(async () => {
        try {
            console.log('Fetching all funders (fallback)...');
            const response = await instance.get('/api/accounts/funders/list/');
            console.log('All funders response:', response.data);
            setFunders(response.data);
        } catch (error) {
            console.error('Error fetching all funders:', error);
            console.error('Error response:', error.response);
        }
    }, []);

    const fetchUserAssignedFunds = useCallback(async (userId) => {
        try {
            console.log('Fetching user assigned funds for user:', userId);
            const response = await instance.get(`/api/accounts/user-fund-assignments/${userId}/assigned_funds/`);
            console.log('User assigned funds response:', response.data);
            
            if (response.data.assigned_funds && response.data.assigned_funds.length > 0) {
                setFunders(response.data.assigned_funds);
                console.log(`Loaded ${response.data.assigned_funds.length} assigned funds for user`);
            } else {
                console.log('No assigned funds found for user, showing empty list');
                setFunders([]);
                // Optionally show a message to user
            }
        } catch (error) {
            console.error('Error fetching user assigned funds:', error);
            console.error('Error response:', error.response);
            // Fallback to all funders if assigned funds fetch fails
            console.log('Falling back to all funders due to error');
            fetchAllFunders();
        }
    }, [fetchAllFunders]);

    const fetchCurrentUser = useCallback(async () => {
        try {
            const response = await instance.get('/api/accounts/user/');
            console.log('Current user:', response.data);
            setCurrentUserId(response.data.id);
            // Once we have user ID, fetch their assigned funds
            if (response.data.id) {
                fetchUserAssignedFunds(response.data.id);
            }
        } catch (error) {
            console.error('Error fetching current user:', error);
            // Fallback to all funders if user ID fetch fails
            fetchAllFunders();
        }
    }, [fetchUserAssignedFunds, fetchAllFunders]);

    useEffect(() => {
        fetchCurrentUser();
    }, [fetchCurrentUser]);

    // Keep original function name for compatibility
    const fetchFunders = fetchAllFunders;

    const validateField = (name, value) => {
        let error = '';

        switch (name) {
            case 'name':
                if (!value.trim()) {
                    error = 'Bursary name is required';
                } else if (value.length < 3) {
                    error = 'Bursary name must be at least 3 characters long';
                }
                break;
            case 'funder':
                if (!value) {
                    error = 'Please select a funder';
                }
                break;
            case 'start_date':
                if (!value) {
                    error = 'Start date is required';
                }
                break;
            case 'end_date':
                if (!value) {
                    error = 'End date is required';
                } else if (formData.start_date && new Date(value) <= new Date(formData.start_date)) {
                    error = 'End date must be after start date';
                }
                break;
            case 'bursary_currency':
                if (!value) {
                    error = 'Currency is required';
                }
                break;
            case 'funding_amount':
                if (!value) {
                    error = 'Funding amount is required';
                } else {
                    const numericValue = parseFloat(value);
                    const decimalRegex = /^\d+(\.\d{1,2})?$/;
                    if (!decimalRegex.test(value) || isNaN(numericValue) || numericValue <= 0) {
                        error = 'Please enter a valid positive amount (max 2 decimal places)';
                    } else if (numericValue > 9999999999.99) {
                        error = 'Amount cannot exceed 9,999,999,999.99';
                    }
                }
                break;
            case 'number_of_beneficiaries':
                if (!value) {
                    error = 'Number of beneficiaries is required';
                } else {
                    const integerValue = parseInt(value);
                    const integerRegex = /^\d+$/;
                    if (!integerRegex.test(value) || isNaN(integerValue) || integerValue <= 0) {
                        error = 'Please enter a valid positive whole number';
                    } else if (integerValue > 999999) {
                        error = 'Number of beneficiaries cannot exceed 999,999';
                    }
                }
                break;
            case 'bursary_manager':
                if (!value.trim()) {
                    error = 'Bursary manager name is required';
                } else if (value.length < 2) {
                    error = 'Manager name must be at least 2 characters long';
                }
                break;
            default:
                break;
        }

        return error;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        let filteredValue = value;

        // Filter input for numeric fields
        if (name === 'funding_amount') {
            // Allow only numbers and one decimal point with up to 2 decimal places
            filteredValue = value.replace(/[^\d.]/g, '');
            const parts = filteredValue.split('.');
            if (parts.length > 2) {
                filteredValue = parts[0] + '.' + parts[1];
            }
            if (parts[1] && parts[1].length > 2) {
                filteredValue = parts[0] + '.' + parts[1].substring(0, 2);
            }
        } else if (name === 'number_of_beneficiaries') {
            // Allow only whole numbers
            filteredValue = value.replace(/[^\d]/g, '');
        }

        setFormData(prev => ({
            ...prev,
            [name]: filteredValue
        }));

        // Validate field on change if it's been touched
        if (touched[name]) {
            const error = validateField(name, filteredValue);
            setErrors(prev => ({
                ...prev,
                [name]: error
            }));
        }
    };

    const handleBlur = (e) => {
        const { name, value } = e.target;
        setTouched(prev => ({
            ...prev,
            [name]: true
        }));

        const error = validateField(name, value);
        setErrors(prev => ({
            ...prev,
            [name]: error
        }));
    };

    const formatDateForSubmission = (dateString) => {
        if (!dateString) return '';
        // Convert date to YYYY-MM-DD format for Django backend
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const validateForm = () => {
        const newErrors = {};
        Object.keys(formData).forEach(field => {
            const error = validateField(field, formData[field]);
            if (error) {
                newErrors[field] = error;
            }
        });

        setErrors(newErrors);
        setTouched(Object.keys(formData).reduce((acc, key) => ({ ...acc, [key]: true }), {}));

        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsLoading(true);
        setSubmitSuccess(false);

        try {
            const submissionData = {
                ...formData,
                start_date: formatDateForSubmission(formData.start_date),
                end_date: formatDateForSubmission(formData.end_date),
                funding_amount: parseFloat(formData.funding_amount),
                number_of_beneficiaries: parseInt(formData.number_of_beneficiaries)
            };

            console.log('Sending bursary data:', submissionData);
            const response = await instance.post('/api/bursaries/', submissionData);

            console.log('Bursary created successfully:', response.data);
            setSubmitSuccess(true);
            
            // Reset form
            setFormData({
                name: '',
                funder: '',
                bmo_bursary_code: '',
                start_date: '',
                end_date: '',
                bursary_currency: '',
                funding_amount: '',
                number_of_beneficiaries: '',
                bursary_manager: ''
            });
            setTouched({});
            setErrors({});

        } catch (error) {
            console.error('Error creating bursary:', error);
            console.error('Error response:', error.response?.data);
            console.error('Error status:', error.response?.status);
            if (error.response?.data) {
                const serverErrors = {};
                Object.keys(error.response.data).forEach(key => {
                    serverErrors[key] = Array.isArray(error.response.data[key]) 
                        ? error.response.data[key][0] 
                        : error.response.data[key];
                });
                setErrors(serverErrors);
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className="mb-0">Create New Bursary</h2>
                <Button 
                    color="secondary" 
                    onClick={() => navigate('/general-dashboard')}
                >
                    Back to Dashboard
                </Button>
            </div>

            {submitSuccess && (
                <Alert color="success" className="mb-4">
                    <i className="fas fa-check-circle me-2"></i>
                    Bursary created successfully! System codes have been auto-generated.
                </Alert>
            )}

            {funders.length === 0 && currentUserId && (
                <Alert color="warning" className="mb-4">
                    <h5 className="alert-heading">
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        No Fund Access
                    </h5>
                    <p className="mb-2">
                        All funds in your BMO have been restricted from your account. To create bursaries, please contact your BMO administrator to remove fund restrictions.
                    </p>
                    <hr />
                    <p className="mb-0">
                        <strong>What to do:</strong> Contact your BMO administrator to request access to the funds you need to manage.
                    </p>
                </Alert>
            )}

            <Form onSubmit={handleSubmit}>
                {/* Bursary Information Section */}
                <Card className="mb-4">
                    <CardBody>
                        <CardTitle tag="h4" className="mb-4">Bursary Information</CardTitle>
                        
                        {/* Row 1: Funder and BMO Bursary Code */}
                        <Row>
                            <Col md={6}>
                                <div className="form-floating mb-3">
                                    <select
                                        className={`form-select ${errors.funder ? 'is-invalid' : ''}`}
                                        id="funder"
                                        name="funder"
                                        value={formData.funder}
                                        onChange={handleInputChange}
                                        onBlur={handleBlur}
                                    >
                                        {funders.length > 0 ? (
                                            <>
                                                <option value="">Select Funder</option>
                                                {funders.map(funder => (
                                                    <option key={funder.id} value={funder.id}>
                                                        {funder.name} ({funder.funder_code})
                                                    </option>
                                                ))}
                                            </>
                                        ) : (
                                            <option value="">No funds available - Contact your administrator</option>
                                        )}
                                    </select>
                                    <label htmlFor="funder">Funder *</label>
                                    {errors.funder && <div className="invalid-feedback">{errors.funder}</div>}
                                    {funders.length === 0 && (
                                        <div className="form-text text-warning">
                                            <i className="fas fa-exclamation-triangle me-1"></i>
                                            All funds in your BMO have been restricted from your account. Please contact your BMO administrator to remove fund restrictions.
                                        </div>
                                    )}
                                    {funders.length > 0 && (
                                        <div className="form-text">
                                            <i className="fas fa-info-circle me-1"></i>
                                            Showing only funds you have been assigned access to ({funders.length} available)
                                        </div>
                                    )}
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="form-floating mb-3">
                                    <input
                                        className="form-control"
                                        type="text"
                                        id="bmo_bursary_code"
                                        name="bmo_bursary_code"
                                        value={formData.bmo_bursary_code}
                                        onChange={handleInputChange}
                                        placeholder="BMO Bursary Code"
                                    />
                                    <label htmlFor="bmo_bursary_code">BMO Bursary Code</label>
                                    <div className="form-text">
                                        <i className="fas fa-info-circle me-1"></i>
                                        Optional: Internal BMO reference code
                                    </div>
                                </div>
                            </Col>
                        </Row>

                        {/* Row 2: Bursary Name */}
                        <Row>
                            <Col md={12}>
                                <div className="form-floating mb-3">
                                    <input
                                        className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        onBlur={handleBlur}
                                        placeholder="Bursary Name"
                                    />
                                    <label htmlFor="name">Bursary Name *</label>
                                    {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                                </div>
                            </Col>
                        </Row>

                        {/* Row 3: Start Date and End Date */}
                        <Row>
                            <Col md={6}>
                                <div className="form-floating mb-3">
                                    <input
                                        className={`form-control ${errors.start_date ? 'is-invalid' : ''}`}
                                        type="date"
                                        id="start_date"
                                        name="start_date"
                                        value={formData.start_date}
                                        onChange={handleInputChange}
                                        onBlur={handleBlur}
                                    />
                                    <label htmlFor="start_date">Start Date *</label>
                                    {errors.start_date && <div className="invalid-feedback">{errors.start_date}</div>}
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="form-floating mb-3">
                                    <input
                                        className={`form-control ${errors.end_date ? 'is-invalid' : ''}`}
                                        type="date"
                                        id="end_date"
                                        name="end_date"
                                        value={formData.end_date}
                                        onChange={handleInputChange}
                                        onBlur={handleBlur}
                                        min={formData.start_date}
                                    />
                                    <label htmlFor="end_date">End Date *</label>
                                    {errors.end_date && <div className="invalid-feedback">{errors.end_date}</div>}
                                </div>
                            </Col>
                        </Row>

                        {/* Row 4: Currency, Funding Amount, and Number of Beneficiaries */}
                        <Row>
                            <Col md={4}>
                                <div className="form-floating mb-3">
                                    <select
                                        className={`form-select ${errors.bursary_currency ? 'is-invalid' : ''}`}
                                        id="bursary_currency"
                                        name="bursary_currency"
                                        value={formData.bursary_currency}
                                        onChange={handleInputChange}
                                        onBlur={handleBlur}
                                    >
                                        <option value="">Select Currency</option>
                                        {currencyOptions.map(currency => (
                                            <option key={currency.value} value={currency.value}>
                                                {currency.label}
                                            </option>
                                        ))}
                                    </select>
                                    <label htmlFor="bursary_currency">Currency *</label>
                                    {errors.bursary_currency && <div className="invalid-feedback">{errors.bursary_currency}</div>}
                                </div>
                            </Col>
                            <Col md={4}>
                                <div className="form-floating mb-3">
                                    <input
                                        className={`form-control ${errors.funding_amount ? 'is-invalid' : ''}`}
                                        type="text"
                                        inputMode="decimal"
                                        id="funding_amount"
                                        name="funding_amount"
                                        value={formData.funding_amount}
                                        onChange={handleInputChange}
                                        onBlur={handleBlur}
                                        placeholder="Total Funding Amount"
                                        style={{ 
                                            MozAppearance: 'textfield',
                                            WebkitAppearance: 'none'
                                        }}
                                    />
                                    <label htmlFor="funding_amount">Total Funding Amount *</label>
                                    {errors.funding_amount && <div className="invalid-feedback">{errors.funding_amount}</div>}
                                    <div className="form-text">
                                        <i className="fas fa-info-circle me-1"></i>
                                        Maximum: 9,999,999,999.99
                                    </div>
                                </div>
                            </Col>
                            <Col md={4}>
                                <div className="form-floating mb-3">
                                    <input
                                        className={`form-control ${errors.number_of_beneficiaries ? 'is-invalid' : ''}`}
                                        type="text"
                                        inputMode="numeric"
                                        id="number_of_beneficiaries"
                                        name="number_of_beneficiaries"
                                        value={formData.number_of_beneficiaries}
                                        onChange={handleInputChange}
                                        onBlur={handleBlur}
                                        placeholder="Number of Beneficiaries"
                                        style={{ 
                                            MozAppearance: 'textfield',
                                            WebkitAppearance: 'none'
                                        }}
                                    />
                                    <label htmlFor="number_of_beneficiaries">Number of Beneficiaries *</label>
                                    {errors.number_of_beneficiaries && <div className="invalid-feedback">{errors.number_of_beneficiaries}</div>}
                                </div>
                            </Col>
                        </Row>

                        {/* Row 5: Bursary Manager */}
                        <Row>
                            <Col md={12}>
                                <div className="form-floating mb-3">
                                    <input
                                        className={`form-control ${errors.bursary_manager ? 'is-invalid' : ''}`}
                                        type="text"
                                        id="bursary_manager"
                                        name="bursary_manager"
                                        value={formData.bursary_manager}
                                        onChange={handleInputChange}
                                        onBlur={handleBlur}
                                        placeholder="Bursary Manager"
                                    />
                                    <label htmlFor="bursary_manager">Bursary Manager *</label>
                                    {errors.bursary_manager && <div className="invalid-feedback">{errors.bursary_manager}</div>}
                                </div>
                            </Col>
                        </Row>

                        {/* Per Beneficiary Calculation */}
                        {formData.funding_amount && formData.number_of_beneficiaries && (
                            <Row className="mb-3">
                                <Col md={12}>
                                    <Alert color="info">
                                        <i className="fas fa-calculator me-2"></i>
                                        <strong>Per Beneficiary Amount:</strong> {formData.bursary_currency} {(parseFloat(formData.funding_amount) / parseInt(formData.number_of_beneficiaries)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </Alert>
                                </Col>
                            </Row>
                        )}
                    </CardBody>
                </Card>

                {/* Auto-Generated Codes Info */}
                <Card className="mb-4">
                    <CardBody>
                        <div className="alert alert-light border">
                            <h6 className="text-primary">
                                <i className="fas fa-cogs me-2"></i>
                                Auto-Generated Codes
                            </h6>
                            <p className="mb-2">
                                <strong>System Bursary Code:</strong> Will be generated automatically (Format: Funder Code + 5-digit Sequence)
                            </p>
                            <p className="mb-2">
                                <strong>Serial ID:</strong> Will be generated from funder name (Format: First 4 letters + -)
                            </p>
                            <p className="mb-0">
                                <strong>Funder Details:</strong> Funder name and code will be auto-populated from selected funder
                            </p>
                        </div>
                    </CardBody>
                </Card>

                {/* Submit Button */}
                <div className="d-flex justify-content-end">
                    <Button
                        color="outline-secondary"
                        className="me-2"
                        onClick={() => {
                            setFormData({
                                name: '',
                                funder: '',
                                bmo_bursary_code: '',
                                start_date: '',
                                end_date: '',
                                bursary_currency: '',
                                funding_amount: '',
                                number_of_beneficiaries: '',
                                bursary_manager: ''
                            });
                            setTouched({});
                            setErrors({});
                        }}
                        disabled={isLoading}
                    >
                        <i className="fas fa-undo me-2"></i>
                        Reset Form
                    </Button>
                    <Button
                        color="primary"
                        type="submit"
                        disabled={isLoading || funders.length === 0}
                    >
                        {isLoading ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Creating...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-save me-2"></i>
                                Create Bursary
                            </>
                        )}
                    </Button>
                </div>
            </Form>
        </div>
    );
};

export default CreateBursary;